import { Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AES, enc } from 'crypto-js';
import { DxDataGridComponent } from 'devextreme-angular';
import { DxDrawerComponent } from 'devextreme-angular';
import config from 'devextreme/core/config';
import DataSource from 'devextreme/data/data_source';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import * as Moment from 'moment-timezone';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { ReportsService } from 'src/app/modules/shared/services/reports.service';
import { environment } from '../../../../../../environments/environment';
import { IAdvancedOrderSearchParams } from '../../../../../shared/models/ocl';
import { FusionAuthService } from '../../../../../shared/services/fusion-auth';
import { PWADetectorService, ScreenService } from '../../../../../shared/services/general';
import { FileUtils } from '../../../../../shared/utils';
import { ClientService } from '../../../../shared/services';
import { PatientService } from '../../../../shared/services';
import { OrdersService } from '../../../../shared/services/orders.service';
import { SettingsService } from '../../../../shared/services/settings.service';
import { TestService } from '../../../../shared/services/test.service';
import { OrderStatus, WebOrder } from '../../models/order';
import { OrderBillingInformation } from '../../models/order-billing-information';
import * as Sentry from "@sentry/angular";

@Component({
  selector: 'app-advanced-search-content',
  templateUrl: './advanced-search-content.component.html',
  styleUrls: ['./advanced-search-content.component.scss'],
})
export class AdvancedSearchContentComponent implements OnInit, OnDestroy {
  public abnormalLabel: string = '';
  public drawerOpened = false;
  public orderId = -1;
  @Input() public showMissingInfo = false;
  private clientHeaderFilterDataSource = new BehaviorSubject(null);
  public clientHeaderFilter = {
    allowSearch: true,
    dataSource: null,
  };
  private lastNameHeaderFilterDataSource = new BehaviorSubject(null);
  public lastNameHeaderFilter = {
    allowSearch: true,
    dataSource: null,
  };
  private firstNameHeaderFilterDataSource = new BehaviorSubject(null);
  public firstNameHeaderFilter = {
    allowSearch: true,
    dataSource: null,
  };
  private testsHeaderFilterDataSource = new BehaviorSubject(null);
  public testsHeaderFilter = {
    allowSearch: true,
    dataSource: null,
  };

  public ackHeaderFilter = [
    {
      text: 'Acknowledged',
      value: ['!', ['onlineAckUser', '=', null]],
    },
    {
      text: 'Not Acknowledged',
      value: ['onlineAckUser', '=', null],
    },
  ];
  public statusHeaderFilter = [
    {
      text: 'PENDING',
      value: [['status', '=', OrderStatus.Pending], 'or', ['status', '=', OrderStatus.Unreleased]],
    },
    {
      text: 'PARTIAL',
      value: [['status', '=', OrderStatus.Partial], 'or', ['status', '=', OrderStatus.PartiallyReleased], 'or', ['status', '=', OrderStatus.Preliminary]],
    },
    {
      text: 'CANCELED',
      value: ['status', '=', OrderStatus.Canceled],
    },
    {
      text: 'FINAL',
      value: ['status', '=', OrderStatus.Final],
    },
  ];
  public criticalResultHeaderFilter = [
    {
      text: 'TOXICOLOGY RESULTS ALERTS',
      value: ['criticalToxResults', '=', true],
    },
    {
      text: 'MICROBIOLOGY RESULTS ALERTS',
      value: ['criticalMicResults', '=', true],
    },
    {
      text: 'CLINICAL RESULTS ALERTS',
      value: ['criticalResults', '=', true],
    }
  ];
  public editHeaderFilter = [
    {
      text: 'MISSING INFORMATION',
      value: ['hasMissingInfo', '=', true],
    }
  ];


  @ViewChild(DxDataGridComponent, { static: true }) public dxDataGridComponent?: DxDataGridComponent;
  @ViewChild(DxDrawerComponent, { static: true }) public drawer?: DxDrawerComponent;
  @Input() public filtersSubmitted: EventEmitter<any> = new EventEmitter<any>();
  public selectedItemsIds: number[] = [];
  public acknowledgeOrderSelected = false;
  public pendingOrderSelected = false;
  public missingInfoPopupVisible = false;
  public missingInfoData: OrderBillingInformation = {} as OrderBillingInformation;
  public isLoading = false;
  public closeDrawer: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  // public labResultsStore: CustomStore;
  // @ts-ignore
  public dataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public filters: IAdvancedOrderSearchParams = {} as IAdvancedOrderSearchParams;
  public emailFaxVisible = false;
  public type: string = '';
  public isPhoneOrTablet: boolean = true;
  public isPhone: boolean = true;
  public webResultSettings: any;
  public user: any;
  public updateBillToVisible = false;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private service: OrdersService,
    private clientService: ClientService,
    private patientService: PatientService,
    private testService: TestService,
    private router: Router,
    private pwaDetectorService: PWADetectorService,
    public route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    public fusionAuthService: FusionAuthService,
    private reportsService: ReportsService,
    private screen: ScreenService,
    public settingsService: SettingsService,
  ) {

  }

  public ngOnInit() {
    if (this.dxDataGridComponent?.instance !== undefined) {
      this.dxDataGridComponent.instance.beginCustomLoading('');
    }

    this.closeDrawer.subscribe(() => this.drawerOpened = false);

    this.responsiveValues();
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.dxDataGridComponent?.instance.clearSelection();
      this.responsiveValues();
    });

    this.settingsService.getWebResultSettings()
      .pipe(take(1))
      .subscribe(x => {
        this.webResultSettings = x;
      });

    this.settingsService.getResultSettings()
      .pipe(take(1))
      .subscribe(results => {
        this.dxDataGridComponent?.instance.endCustomLoading();
        this.abnormalLabel = results.abnormalResult;
      });

    this.waitByUser();
  }

  private waitByUser() {
    const parameterUser = (this.route.queryParams as any).value.user;

    if (parameterUser) {
      try {
        this.user = JSON.parse(AES.decrypt(parameterUser, environment.cryptoPassphrase).toString(enc.Utf8));
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    if (!this.user) {
      this.user = this.fusionAuthService.Profile;
    }

    if (this.user) {
      this.initGridHeaderFilters();
      this.filtersSubmitted
        .pipe(
          takeUntil(this.onDestroy$),
          debounceTime(1000),
        )
        .subscribe(filters => {
          this.filters = filters;
          this.setFilters(this.filters, this.user);
        });
    } else {
      of()
        .pipe(
          debounceTime(1000),
          take(1),
        )
        .subscribe(() => this.waitByUser());
    }
  }

  private responsiveValues() {
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] ||
      this.screen.sizes['screen-small'] ||
      this.screen.sizes['screen-tablet'];
    this.isPhone = this.screen.sizes['screen-x-small'];

    const offset = this.isPhone ? '-15 75' : '-25 75';
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        position: {
          of: 'body',
          my: 'right top',
          at: 'right top',
          offset,
        },
      },
    });
    repaintFloatingActionButton();
  }

  public get ButtonsDisabled() {
    return this.selectedItemsIds.length === 0;
  }

  public ack() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Acknowledging');
    let queryString = '';
    this.selectedItemsIds.map(x => {
      queryString += `ids=${x}&`;
    });
    this.service.acknowledgeOrder(queryString)
      .pipe(take(1))
      .subscribe(() => {
        this.selectedItemsIds = [];
        this.dxDataGridComponent?.instance.getDataSource().reload();
        this.dxDataGridComponent?.instance.clearSelection();
        this.dxDataGridComponent?.instance.endCustomLoading();
      });
  }

  public setFilters(filters: IAdvancedOrderSearchParams, user: any) {
    const role = user.registrations[0].roles[0];
    switch (role) {
      case 'Client Admin':
      case 'Client Basic':
        const clientIds = user ? user.data.clients : this.fusionAuthService.Profile.data.clients;
        this.filters.loggedClientIds = clientIds;
        break;
      case 'Patient':
        const patientIds = user ? user.data.patients : this.fusionAuthService.Profile.data.patients;
        this.filters.loggedPatientIds = patientIds;
        break;
      case 'Physician':
        const physicianIds = user ? user.data.physicians : this.fusionAuthService.Profile.data.physicians;
        this.filters.loggedPhysicianIds = physicianIds;
        break;
    }
    this.dataSource.next(new DataSource({
      store:
        this.service.advancedSearchStore(filters),
    }));
  }

  public printLabResults() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderResults(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public printOrderLog() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderLog(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public printOrderDetails() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderDetails(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public openEmailFax(type: string) {
    this.type = type;
    this.emailFaxVisible = true;
  }

  public openUpdateBillTo() {
    this.updateBillToVisible = true;
  }

  public closeUpdateBillTo() {
    this.updateBillToVisible = false;
    this.setFilters(this.filters, this.user);
  }

  public get FABDisabled() {
    return this.isPhoneOrTablet && this.selectedItemsIds ? this.selectedItemsIds.length > 0 : false;
  }

  public showColumnChooser() {
    this.dxDataGridComponent?.instance.showColumnChooser();
  }

  public onContentReady(): void {
    this.dxDataGridComponent?.instance.clearSelection();
  }

  public itemsSelected(ids: number[]) {
    this.selectedItemsIds = ids;
    if (!this.dxDataGridComponent?.instance) {
      return;
    }
    const ackFilter = this.dxDataGridComponent.instance.getSelectedRowsData().filter((r: WebOrder) => r.onlineAckUser);
    if (Array.isArray(ackFilter)) {
      this.acknowledgeOrderSelected = ackFilter.length !== 0;
    }

    const pendingOrderFiler = this.dxDataGridComponent.instance.getSelectedRowsData()
      .filter((r: WebOrder) => r.status === OrderStatus.Pending || r.status === OrderStatus.Unreleased);
    if (Array.isArray(pendingOrderFiler)) {
      this.pendingOrderSelected = pendingOrderFiler.length !== 0;
    }
  }

  public testResult(testId: number, order: any) {
    const test = order.orderResults.find((x: any) => x.testId === testId);
    if (test) {
      if (test.reportAs) {
        return test.flag === null || test.flag === '' ? test.reportAs : `${test.reportAs} (${test.flag})`;
      } else {
        return test.flag === null || test.flag === '' ? test.results : `${test.results} (${test.flag})`;
      }

    } else {
      return 'NOT ORDERED';
    }
  }

  public closeMissingInfo(canceled: boolean) {
    this.missingInfoPopupVisible = false;
    if (!canceled) {
      this.setFilters(this.filters, this.user);
    }
  }

  public openMissingInfo({ data }: { data: any}) {
      this.isLoading = true;
      this.service.getOrderBillingInfo(data.id)
          .pipe(take(1))
          .subscribe(res => {
              this.missingInfoData = res;
              this.missingInfoPopupVisible = true;
              this.isLoading = false;
          });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public openPreview(id: number) {
    this.drawerOpened = !this.drawerOpened;
    this.orderId = id;
  }

  public get UserRole() {
    const user = this.user ? this.user : this.fusionAuthService.Profile;
    return user.registrations[0].roles[0];
  }

  public get UserClients() {
    if (!this.UserRole) {
      return [];
    }
    if (!this.userRoleInArray(['Client Admin', 'Client Basic'])) {
      return [];
    }
    return this.user ? this.user.data.clients : this.fusionAuthService.Profile.data.clients;
  }

  public get UserPhysicians() {
    if (!this.UserRole) {
      return [];
    }
    if (!this.userRoleInArray(['Physician'])) {
      return [];
    }
    return this.user ? this.user.data.physicians : this.fusionAuthService.Profile.data.physicians;
  }

  public get UserPatients() {
    if (!this.UserRole) {
      return [];
    }
    if (!this.userRoleInArray(['Patient'])) {
      return [];
    }
    return this.user ? this.user.data.physicians : this.fusionAuthService.Profile.data.physicians;
  }

  public getStatus(order: WebOrder) {
    let color;
    let status: string = OrderStatus[OrderStatus.None];
    switch (order.status) {
      case OrderStatus.Canceled:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.Canceled];
        break;
      case OrderStatus.Final:
        color = 'ocl-status-green';
        status = OrderStatus[OrderStatus.Final];
        break;
      case OrderStatus.Partial:
      case OrderStatus.PartiallyReleased:
      case OrderStatus.Preliminary:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Partial];
        break;
      case OrderStatus.Pending:
      case OrderStatus.Unreleased:
        color = 'ocl-status-yellow';
        status = OrderStatus[OrderStatus.Pending];
        break;
      case OrderStatus.None:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.None];
        break;
    }

    let container = `
            <div fxLayout='column' fxLayoutAlign='center'>
                <div style='width: 100px;height:15px!important;line-height: .45 !important' class='${color}'>
                ${status.toUpperCase()}</div>
        `;
    container += `</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(container);
  }

  public getCriticalResultStatus(order: WebOrder) {
    let alert = `
            <div fxLayout='column' fxLayoutAlign='center'>
        `;

    if (order.hasAlert) {
      if (order.criticalToxResults) {
        alert += `<div style='width: 100px;height:15px!important;line-height: .45 !important' class='ocl-status-critical'>
                    ${this.abnormalLabel.toUpperCase()}</div>`;
      }
      if (order.criticalMicResults || order.criticalResults) {
        alert += `<div style='width: 100px;height:15px!important;line-height: .45 !important' class='ocl-status-critical'>CRITICAL</div>`;
      }
    }
    alert += `</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(alert);
  }

  public initGridHeaderFilters() {
    this.clientHeaderFilterDataSource
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(dataSource => {
        if (dataSource) {
          this.clientHeaderFilter.dataSource = dataSource;
        }
      });
    // @ts-ignore
    this.clientHeaderFilterDataSource.next({
      // @ts-ignore
      store: this.clientService.advancedSearchStore(this.clientHeaderFilterDataSource, this.UserRole, this.UserClients),
    });

    this.lastNameHeaderFilterDataSource
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(dataSource => {
        if (dataSource) {
          this.lastNameHeaderFilter.dataSource = dataSource;
        }
      });
    // @ts-ignore
    this.lastNameHeaderFilterDataSource.next({
      // @ts-ignore
      store: this.patientService.getPatientField(this.lastNameHeaderFilterDataSource, this.UserRole, 'lastname', this.UserClients, this.UserPatients, this.UserPhysicians),
    });

    this.firstNameHeaderFilterDataSource
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(dataSource => {
        if (dataSource) {
          this.firstNameHeaderFilter.dataSource = dataSource;
        }
      });
    // @ts-ignore
    this.firstNameHeaderFilterDataSource.next({
      // @ts-ignore
      store: this.patientService.getPatientField(this.firstNameHeaderFilterDataSource, this.UserRole, 'firstname', this.UserClients, this.UserPatients, this.UserPhysicians),
    });

    this.testsHeaderFilterDataSource
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(dataSource => {
        if (dataSource) {
          this.testsHeaderFilter.dataSource = dataSource;
        }
      });
    // @ts-ignore
    this.testsHeaderFilterDataSource.next({
      // @ts-ignore
      store: this.testService.advancedSearchStore(this.testsHeaderFilterDataSource, this.UserRole, this.UserClients),
    });
  }

  public userRoleInArray(arr: any[]) {
    if (!arr) {
      return false;
    }
    const role = this.UserRole;
    return arr.includes(role);
  }

  public formatDate(date: Date) {
    const tz = Moment.tz.guess();
    return Moment.utc(date).tz(tz).format('LLLL');
  }
}
